export interface ResultSummaryItem {
  label: string;
  value: string;
}

export function extractBaseExposure(
  response: any,
): number | null {
  const firstArrayItem =
    Array.isArray(response)
      ? response[0]
      : undefined;

  const candidates: unknown[] = [
    response,

    response?.baseExposure,
    response?.maxExposure,
    response?.maximumExposure,
    response?.previousMaxExposure,
    response?.prvsMaxExpsr,
    response?.prvsMaxExposure,
    response?.maxExpsr,
    response?.value,
    response?.val,

    response?.data,
    response?.data?.baseExposure,
    response?.data?.maxExposure,
    response?.data?.maximumExposure,
    response?.data?.previousMaxExposure,
    response?.data?.prvsMaxExpsr,
    response?.data?.prvsMaxExposure,
    response?.data?.maxExpsr,
    response?.data?.value,
    response?.data?.val,

    firstArrayItem?.baseExposure,
    firstArrayItem?.maxExposure,
    firstArrayItem?.prvsMaxExpsr,
    firstArrayItem?.maxExpsr,
    firstArrayItem?.value,
    firstArrayItem?.val,
  ];

  for (const candidate of candidates) {
    if (
      candidate === null ||
      candidate === undefined ||
      candidate === ''
    ) {
      continue;
    }

    if (typeof candidate === 'object') {
      continue;
    }

    const numericValue = Number(candidate);

    if (
      Number.isFinite(numericValue) &&
      numericValue >= 0
    ) {
      return numericValue;
    }
  }

  return null;
}

export function extractExistingRepayment(
  response: any,
): number | null {
  const firstArrayItem =
    Array.isArray(response)
      ? response[0]
      : undefined;
  const candidates: unknown[] = [
    response,
    response?.existingRepayment,
    response?.existingRepaymentAmount,
    response?.xstngRpymntAmt,
    response?.xstngRpymnt,
    response?.value,
    response?.val,
    response?.data,
    response?.data?.existingRepayment,
    response?.data?.existingRepaymentAmount,
    response?.data?.xstngRpymntAmt,
    response?.data?.xstngRpymnt,
    response?.data?.value,
    response?.data?.val,
    firstArrayItem?.existingRepayment,
    firstArrayItem?.existingRepaymentAmount,
    firstArrayItem?.xstngRpymntAmt,
    firstArrayItem?.xstngRpymnt,
    firstArrayItem?.value,
    firstArrayItem?.val,
  ];
  for (const candidate of candidates) {
    if (
      candidate === null ||
      candidate === undefined ||
      candidate === ''
    ) {
      continue;
    }
    if (typeof candidate === 'object') {
      continue;
    }
    const numericValue = Number(candidate);
    if (
      Number.isFinite(numericValue) &&
      numericValue >= 0
    ) {
      return numericValue;
    }
  }
  return null;
}

export function extractExistingDisbursement(
  response: any,
): number | null {
  const firstArrayItem =
    Array.isArray(response)
      ? response[0]
      : undefined;
  const candidates: unknown[] = [
    response,
    response?.existingDisbursement,
    response?.existingDisbursementAmount,
    response?.xstngDsbmntAmt,
    response?.xstngDsbmnt,
    response?.value,
    response?.val,
    response?.data,
    response?.data?.existingDisbursement,
    response?.data?.existingDisbursementAmount,
    response?.data?.xstngDsbmntAmt,
    response?.data?.xstngDsbmnt,
    response?.data?.value,
    response?.data?.val,
    firstArrayItem?.existingDisbursement,
    firstArrayItem?.existingDisbursementAmount,
    firstArrayItem?.xstngDsbmntAmt,
    firstArrayItem?.xstngDsbmnt,
    firstArrayItem?.value,
    firstArrayItem?.val,
  ];
  for (const candidate of candidates) {
    if (
      candidate === null ||
      candidate === undefined ||
      candidate === ''
    ) {
      continue;
    }
    if (typeof candidate === 'object') {
      continue;
    }
    const numericValue = Number(candidate);
    if (
      Number.isFinite(numericValue) &&
      numericValue >= 0
    ) {
      return numericValue;
    }
  }
  return null;
}

export function extractResultRows(
  response: any,
): Record<string, unknown>[] {
  const possibleLists = [
    response,

    response?.data,
    response?.content,
    response?.data?.content,

    response?.repaymentPlan,
    response?.repaymentPlanList,

    response?.rpymntPlan,
    response?.rpymntPlanList,

    response?.installments,
    response?.installmentList,

    response?.schedule,
    response?.scheduleList,

    response?.data?.repaymentPlan,
    response?.data?.repaymentPlanList,

    response?.data?.rpymntPlan,
    response?.data?.rpymntPlanList,

    response?.data?.installments,
    response?.data?.installmentList,

    response?.data?.schedule,
    response?.data?.scheduleList,
  ];

  const matchedList = possibleLists.find(
    (value: unknown) => Array.isArray(value),
  );

  if (!Array.isArray(matchedList)) {
    return [];
  }

  return matchedList.map(
    (item: unknown): Record<string, unknown> => {
      if (
        item !== null &&
        typeof item === 'object' &&
        !Array.isArray(item)
      ) {
        return item as Record<string, unknown>;
      }

      return { value: item };
    },
  );
}

export function extractResultColumns(
  rows: Record<string, unknown>[],
): string[] {
  const columns = new Set<string>();

  rows.forEach((row: Record<string, unknown>) => {
    Object.keys(row).forEach((key: string) => {
      columns.add(key);
    });
  });

  return Array.from(columns);
}

export function formatColumnLabel(
  value: string,
): string {
  return String(value ?? '')
    .replace(/([a-z0-9])([A-Z])/g, '$1 $2')
    .replace(/_/g, ' ')
    .replace(/\b\w/g, (character: string) =>
      character.toUpperCase(),
    )
    .trim();
}

export function formatResultValue(
  value: unknown,
): string {
  if (
    value === null ||
    value === undefined ||
    value === ''
  ) {
    return '-';
  }

  if (typeof value === 'number') {
    return new Intl.NumberFormat('en-US', {
      maximumFractionDigits: 2,
    }).format(value);
  }

  if (typeof value === 'boolean') {
    return value ? 'Yes' : 'No';
  }

  if (typeof value === 'object') {
    try {
      return JSON.stringify(value);
    } catch {
      return String(value);
    }
  }

  return String(value);
}

export function extractResultSummary(
  response: any,
): ResultSummaryItem[] {
  if (
    response === null ||
    response === undefined ||
    Array.isArray(response)
  ) {
    return [];
  }

  const source =
    response?.data !== null &&
    response?.data !== undefined &&
    !Array.isArray(response.data) &&
    typeof response.data === 'object'
      ? response.data
      : response;

  if (
    source === null ||
    typeof source !== 'object' ||
    Array.isArray(source)
  ) {
    return [];
  }

  return Object.entries(source)
    .filter(
      ([_key, value]) =>
        value !== null &&
        value !== undefined &&
        !Array.isArray(value) &&
        typeof value !== 'object',
    )
    .map(
      ([key, value]): ResultSummaryItem => ({
        label: formatColumnLabel(key),
        value: formatResultValue(value),
      }),
    );
}
